import { useCallback, useState } from 'react';
import { InputProps } from 'modules/queryBuilder/contexts';
import { isNull, isString } from 'utils/tsHelpers';
import { isEmptyString } from 'utils/stringHelpers';
import { InputTextProps } from 'ui/inputs';
import { SelectValueType } from 'ui/selects';

import { getOnInputChange } from './utils';

type Config<TValue> = {
  value: TValue;
  setIsEditing: (status: boolean) => void;
} & Pick<InputTextProps, 'type' | 'maxValue' | 'minValue'> &
  Pick<InputProps<TValue>, 'onChange'>;

export const useConditionInputWithoutOptionsState = <TValue extends SelectValueType = string>({
  value,
  onChange,
  type,
  setIsEditing,
  maxValue,
  minValue,
}: Config<TValue | null>): {
  setInputValue: (inputVal: string) => void;
  inputValue: string;
  onUpdateValue: () => void;
} => {
  const isEmpty = isNull(value) || (isString(value) && isEmptyString(value));
  const [inputValue, setInputValue] = useState<string>(value?.toString() ?? '');
  const onInputChange = getOnInputChange({ onChange, type, isEmpty });

  const onUpdateValue = useCallback(() => {
    onInputChange(inputValue);
    setIsEditing(false);
  }, [inputValue, onInputChange, setIsEditing]);

  // prevent numbers from reaching max and min here
  const onUpdateInputValue = useCallback(
    (val: string) => {
      if (type !== 'number') setInputValue(val);
      if (maxValue && Number(val) > maxValue) return;
      if (minValue && Number(val) < minValue) return;
      else setInputValue(val);
    },
    [type, maxValue, minValue],
  );

  return {
    setInputValue: onUpdateInputValue,
    inputValue,
    onUpdateValue,
  };
};
